if (!customElements.get('gallery-modal')) {
  const galleryPreloadCache = new Set();

  function preloadGalleryImage(url) {
    if (!url || galleryPreloadCache.has(url)) {
      return Promise.resolve();
    }

    galleryPreloadCache.add(url);

    return new Promise((resolve) => {
      const img = new Image();
      img.onload = img.onerror = () => resolve();
      img.src = url;
    });
  }

  function getCardThumbnail(opener) {
    const cardImg = opener.closest('.ez-gallery-card__opener')?.querySelector('.ez-gallery-card__image');
    return cardImg?.currentSrc || cardImg?.src || '';
  }

  if (!window.__ezGalleryPreloadBound) {
    window.__ezGalleryPreloadBound = true;

    const preloadFromToggle = (event) => {
      const toggle = event.target.closest?.('.ez-gallery-card__toggle');
      const fullSrc = toggle?.dataset.galleryImageSrc;
      if (fullSrc) preloadGalleryImage(fullSrc);
    };

    document.addEventListener('pointerenter', preloadFromToggle, true);
    document.addEventListener('focusin', preloadFromToggle, true);
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-ez-gallery-teaser-open-first]');
    if (!trigger) return;

    const controlsId = trigger.getAttribute('aria-controls');
    const modal = controlsId ? document.getElementById(controlsId) : null;
    const sectionRoot = trigger.closest('[id^="shopify-section-"]');
    const firstToggle = sectionRoot?.querySelector(
      '.ez-gallery-teaser__pdp-preview .ez-gallery-card__toggle'
    );

    if (!modal || !firstToggle || typeof modal.show !== 'function') return;

    event.preventDefault();
    modal.show(firstToggle);
  });

  customElements.define(
    'gallery-modal',
    class GalleryModal extends ModalDialog {
      constructor() {
        super();
        this.imageEl = this.querySelector('.ez-gallery-modal__image');
        this.titleEl = this.querySelector('.ez-gallery-modal__title');
        this.captionEl = this.querySelector('.ez-gallery-modal__caption');
        this.productRow = this.querySelector('.ez-gallery-modal__product-row');
        this.productLink = this.querySelector('.ez-gallery-modal__product');
        this.productLinkText = this.querySelector('.ez-gallery-modal__product-text');
        this.linkRow = this.querySelector('.ez-gallery-modal__link-row');
        this.customLink = this.querySelector('.ez-gallery-modal__link');
        this.customLinkText = this.querySelector('.ez-gallery-modal__link-text');
        this.dialogEl = this.querySelector('[role="dialog"]');
        this.counterEl = this.querySelector('.ez-gallery-modal__counter');
        this.slideLogoEl = this.querySelector('.ez-gallery-modal__slide-logo');
        this.slideBodyEl = this.querySelector('.ez-gallery-modal__slide-body');
        this.endPanelEl = this.querySelector('.ez-gallery-modal__end-panel');
        this.mediaRowEl = this.querySelector('.ez-gallery-modal__media-row');
        this.prevBtn = this.querySelector('[data-gallery-nav="prev"]');
        this.nextBtn = this.querySelector('[data-gallery-nav="next"]');
        this.openers = [];
        this.currentIndex = -1;
        this.imageLoadToken = 0;
        this.boundKeydown = this.onKeydown.bind(this);
        this.boundTouchStart = this.onTouchStart.bind(this);
        this.boundTouchEnd = this.onTouchEnd.bind(this);
        this.endCardEnabled = this.dataset.endCard === 'true';
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.swipeSurface = this.querySelector('.ez-gallery-modal__media-row');

        if (this.swipeSurface) {
          this.swipeSurface.addEventListener('touchstart', this.boundTouchStart, { passive: true });
          this.swipeSurface.addEventListener('touchend', this.boundTouchEnd, { passive: true });
        }

        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', (event) => {
            event.preventDefault();
            this.step(-1);
          });
        }

        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', (event) => {
            event.preventDefault();
            this.step(1);
          });
        }
      }

      get endCardIndex() {
        return this.endCardEnabled ? this.openers.length : -1;
      }

      get photoCount() {
        return this.openers.length;
      }

      get totalSlides() {
        const count = this.photoCount;
        if (!count) return 0;
        return this.endCardEnabled ? count + 1 : count;
      }

      isEndCardIndex(index) {
        return this.endCardEnabled && index === this.endCardIndex;
      }

      show(opener) {
        this.refreshOpeners();
        this.currentIndex = this.openers.indexOf(opener);
        if (this.currentIndex < 0) this.currentIndex = 0;

        this.renderSlide(this.currentIndex);
        super.show(opener);
        document.addEventListener('keydown', this.boundKeydown);
        this.updateNavState();
        this.preloadAdjacent();
      }

      hide() {
        document.removeEventListener('keydown', this.boundKeydown);
        this.imageLoadToken += 1;
        this.classList.remove('is-end-card');
        super.hide();
      }

      refreshOpeners() {
        const sectionId = this.dataset.sectionId;
        const sectionRoot = sectionId
          ? document.querySelector(`#shopify-section-${sectionId}`)
          : this.closest('.shopify-section');

        if (!sectionRoot) {
          this.openers = [];
          return;
        }

        // Follow the active filter, including matches still behind Load more. Teaser grids
        // have no filterable wrappers, so they fall back to every toggle in the section.
        const items = Array.from(sectionRoot.querySelectorAll('[data-ez-gallery-item]'));

        if (!items.length) {
          this.openers = Array.from(sectionRoot.querySelectorAll('.ez-gallery-card__toggle'));
          return;
        }

        this.openers = items
          .filter((item) => item.dataset.filteredOut !== 'true')
          .map((item) => item.querySelector('.ez-gallery-card__toggle'))
          .filter(Boolean);
      }

      step(delta) {
        if (this.totalSlides < 2) return;

        const nextIndex = this.currentIndex + delta;
        if (nextIndex < 0 || nextIndex >= this.totalSlides) return;

        this.currentIndex = nextIndex;
        this.renderSlide(this.currentIndex);
        this.updateNavState();
        this.preloadAdjacent();
      }

      preloadAdjacent() {
        if (this.isEndCardIndex(this.currentIndex) || this.openers.length < 1) return;

        [-1, 1].forEach((delta) => {
          const index = this.currentIndex + delta;
          if (index < 0 || index >= this.openers.length) return;
          const src = this.openers[index]?.dataset.galleryImageSrc;
          if (src) preloadGalleryImage(src);
        });
      }

      updateNavState() {
        const total = this.totalSlides;
        const showNav = total > 1;

        if (this.prevBtn) {
          this.prevBtn.hidden = !showNav || this.currentIndex <= 0;
        }

        if (this.nextBtn) {
          this.nextBtn.hidden = !showNav || this.currentIndex >= total - 1;
        }

        this.updateCounter();
      }

      updateCounter() {
        if (!this.counterEl) return;

        const photos = this.photoCount;
        if (photos < 1 || this.currentIndex < 0) {
          this.counterEl.textContent = '\u00a0';
          this.counterEl.setAttribute('aria-hidden', 'true');
          return;
        }

        if (this.isEndCardIndex(this.currentIndex)) {
          this.counterEl.textContent = '\u00a0';
          this.counterEl.setAttribute('aria-hidden', 'true');
          return;
        }

        this.counterEl.textContent = `${this.currentIndex + 1} of ${photos}`;
        this.counterEl.removeAttribute('aria-hidden');
      }

      onKeydown(event) {
        if (!this.hasAttribute('open')) return;

        if (event.code === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        } else if (event.code === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        }
      }

      onTouchStart(event) {
        if (!this.hasAttribute('open')) return;
        if (!window.matchMedia('(max-width: 989px)').matches) return;

        const touch = event.touches[0];
        if (!touch) return;

        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
      }

      onTouchEnd(event) {
        if (!this.hasAttribute('open')) return;
        if (!window.matchMedia('(max-width: 989px)').matches) return;

        const touch = event.changedTouches[0];
        if (!touch) return;

        const dx = touch.clientX - this.touchStartX;
        const dy = touch.clientY - this.touchStartY;
        const threshold = 50;

        if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy)) return;

        if (dx < 0) {
          this.step(1);
        } else {
          this.step(-1);
        }
      }

      renderSlide(index) {
        if (this.isEndCardIndex(index)) {
          this.renderEndCard();
          return;
        }

        const opener = this.openers[index];
        if (!opener) return;
        this.openedBy = opener;

        const item = opener.closest('[data-ez-gallery-item]');
        if (item?.hidden) {
          item.dispatchEvent(new CustomEvent('ez-gallery:reveal', { bubbles: true }));
        }

        this.updateContent(opener);
      }

      setEndCardMode(active) {
        this.classList.toggle('is-end-card', active);

        if (this.slideLogoEl) this.slideLogoEl.hidden = active;
        if (this.slideBodyEl) this.slideBodyEl.hidden = active;
        if (this.endPanelEl) this.endPanelEl.hidden = !active;
      }

      renderEndCard() {
        this.imageLoadToken += 1;
        this.setEndCardMode(true);

        const endImageSrc = this.dataset.endCardImageSrc || '';
        const endImageAlt = this.dataset.endCardImageAlt || '';

        if (this.imageEl) {
          if (endImageSrc) {
            this.imageEl.alt = endImageAlt;
            this.imageEl.src = endImageSrc;
            this.imageEl.hidden = false;
            this.imageEl.classList.remove('is-loading');
            preloadGalleryImage(endImageSrc);
          } else {
            this.imageEl.removeAttribute('src');
            this.imageEl.alt = '';
            this.imageEl.hidden = true;
            this.imageEl.classList.remove('is-loading');
          }
        }

        if (this.dialogEl) {
          const photos = this.photoCount;
          const position = photos ? `, ${photos} of ${photos}` : '';
          this.dialogEl.setAttribute('aria-label', `Gallery: Get started with EZ Louver${position}`);
        }

        this.updateCounter();
      }

      setModalImage(opener, imageSrc, imageAlt) {
        if (!this.imageEl) return;

        const loadToken = ++this.imageLoadToken;
        const thumbSrc = getCardThumbnail(opener);
        const instantSrc = thumbSrc || imageSrc;

        if (!instantSrc) {
          this.imageEl.removeAttribute('src');
          this.imageEl.alt = '';
          this.imageEl.hidden = true;
          this.imageEl.classList.remove('is-loading');
          return;
        }

        this.imageEl.alt = imageAlt;
        this.imageEl.hidden = false;
        this.imageEl.src = instantSrc;
        this.imageEl.classList.toggle('is-loading', !!(imageSrc && imageSrc !== instantSrc));

        if (!imageSrc || imageSrc === instantSrc) {
          this.imageEl.classList.remove('is-loading');
          return;
        }

        preloadGalleryImage(imageSrc).then(() => {
          if (loadToken !== this.imageLoadToken) return;
          this.imageEl.src = imageSrc;
          this.imageEl.classList.remove('is-loading');
        });
      }

      updateContent(opener) {
        if (!opener) return;

        this.setEndCardMode(false);

        const imageSrc = opener.dataset.galleryImageSrc || '';
        const imageAlt = opener.dataset.galleryImageAlt || '';
        const title = opener.dataset.galleryTitle || '';
        const productUrl = opener.dataset.galleryProductUrl || '';
        const productLabel = opener.dataset.galleryProductLabel || '';
        const linkUrl = opener.dataset.galleryLinkUrl || '';
        const linkLabel = opener.dataset.galleryLinkLabel || '';
        const captionId = opener.dataset.galleryCaptionId || '';

        this.setModalImage(opener, imageSrc, imageAlt);

        if (this.titleEl) {
          this.titleEl.textContent = title;
          this.titleEl.hidden = !title;
        }

        if (this.productRow && this.productLink) {
          if (productUrl) {
            this.productLink.href = productUrl;
            if (this.productLinkText) {
              this.productLinkText.textContent = productLabel || 'View product';
            }
            this.productRow.hidden = false;
          } else {
            this.productLink.removeAttribute('href');
            this.productRow.hidden = true;
          }
        }

        if (this.linkRow && this.customLink) {
          if (linkUrl) {
            this.customLink.href = linkUrl;
            if (this.customLinkText) {
              this.customLinkText.textContent = linkLabel || 'Learn more';
            }
            this.linkRow.hidden = false;
          } else {
            this.customLink.removeAttribute('href');
            this.linkRow.hidden = true;
          }
        }

        if (this.captionEl) {
          this.captionEl.innerHTML = '';
          if (captionId) {
            const template = document.getElementById(captionId);
            if (template && template.content) {
              this.captionEl.appendChild(template.content.cloneNode(true));
              this.captionEl.hidden = false;
            } else {
              this.captionEl.hidden = true;
            }
          } else {
            this.captionEl.hidden = true;
          }
        }

        if (this.dialogEl) {
          const photos = this.photoCount;
          const position = photos ? `${this.currentIndex + 1} of ${photos}` : '';
          const label = title ? `Gallery: ${title}${position ? `, ${position}` : ''}` : 'Gallery image';
          this.dialogEl.setAttribute('aria-label', label);
        }

        this.updateCounter();
      }
    }
  );
}
